import { Alert, Platform, Pressable, Text, View } from 'react-native';
import useConfirmationStore from '../data/store/useConfirmationStore';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useEffect, useState } from 'react';
import SolidButton from '../components/General/SolidButton';
import { StyleSheet } from 'react-native-unistyles';

const ConfirmationModal = () => {
    const confirmation = useConfirmationStore();

    const [isOpen, setIsOpen] = useState(false);

    const fade = useSharedValue(0);
    const fadeStyle = useAnimatedStyle(() => {
        return {
            opacity: fade.value,
        }
    })

    useEffect(() => {
        if (confirmation.isOpen) {
            setIsOpen(true)
            fade.value = withTiming(1, {duration: 300});
        } else {
            setIsOpen(false);
        }
    }, [confirmation.isOpen]);

    const dismiss = () => {
        fade.value = withTiming(0, {duration: 300});
        setTimeout(() => { useConfirmationStore.getState().toggleIsOpen(); }, 300);
    };

    return (
        <>
            {isOpen &&
                <Animated.View style={[fadeStyle, styles.container]}>
                    <Pressable onPress={dismiss} style={styles.overlay}></Pressable>
                    
                    <View style={styles.modalContainer}>
                        <Text style={styles.title}>{confirmation.options?.title}</Text>
                        <Text style={styles.subtitle}>{confirmation.options?.subtitle}</Text>

                        <View style={styles.buttonContainer}>
                            {!!confirmation.options?.cancelText && (
                                <SolidButton testID="alert-cancel-button" variant="outlined" size='sm' text={confirmation.options?.cancelText} onPress={() => {
                                    if(confirmation.options?.onCancel){
                                        confirmation.options?.onCancel();
                                    }
                                    dismiss();
                                }} type="textSecondary" />
                            )}

                            {confirmation.options?.extraButton && (
                                <SolidButton
                                    testID="alert-extra-button"
                                    type={confirmation.options.extraButton.isDestructive ? "error" : "primary"}
                                    size='sm'
                                    text={confirmation.options.extraButton.text}
                                    onPress={() => {
                                        if(confirmation.options.extraButton.onPress){
                                            confirmation.options.extraButton.onPress();
                                        }
                                        dismiss();
                                    }}
                                />
                            )}
                            
                            {!!confirmation.options?.confirmText && (
                                <SolidButton testID="alert-confirm-button" type={confirmation.options?.isDestructive ? "error" : "primary"} size='sm' text={confirmation.options?.confirmText} onPress={() => {
                                    if(confirmation.options?.onConfirm){
                                        confirmation.options?.onConfirm();
                                    }
                                    dismiss();
                                }} />
                            )}
                        </View>
                    </View> 
                </Animated.View>
            }
       </>
    );
};

const showAlert = ({title, subtitle, onCancel = () => {}, onConfirm = () => {}, cancelText = "Cancel", confirmText = "Confirm", isDestructive = false, extraButton = null}) => {
  if (Platform.OS === 'web') {
    useConfirmationStore.getState().toggleIsOpen({title, subtitle, onCancel, onConfirm, cancelText, confirmText, isDestructive, extraButton});
  } else {
    const buttons = [];
    if (cancelText) {
      buttons.push({ text: cancelText, onPress: onCancel, style: 'cancel' });
    }
    if (extraButton) {
      buttons.push({ text: extraButton.text, onPress: extraButton.onPress, style: extraButton.isDestructive ? 'destructive' : 'default' });
    }
    if (confirmText) {
      buttons.push({ text: confirmText, onPress: onConfirm, style: isDestructive ? 'destructive' : 'default' });
    }
    Alert.alert(title, subtitle, buttons);
  }
};

const styles = StyleSheet.create(({colors, fonts, shadows}, rt) => ({
    container: {
        backgroundColor:"rgba(0,0,0,0.7)", 
        position:"absolute", 
        width:rt.screen.width, 
        height:rt.screen.height, 
        alignItems:"center", 
        justifyContent:"center", 
        zIndex:10000
    },
    overlay: {
        position:"absolute", 
        width:rt.screen.width, 
        height:rt.screen.height, 
        cursor:"auto"
    },
    modalContainer: {
        width:400, 
        padding:20, 
        boxShadow: shadows.card, 
        borderRadius:8, 
        backgroundColor:colors.background, 
        position:'fixed', 
        top:"50%", 
        left:"50%", 
        transform:[{translateX:"-50%"}, {translateY:"-50%"}],
        zIndex: 10001
    },
    title: {
        fontFamily: fonts.heading,
        fontSize: 20,
        color: colors.text,
        paddingBottom: 10
    },
    subtitle: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body
    },
    buttonContainer: {
        flexDirection:"row", 
        justifyContent:"flex-end", 
        paddingTop:20, 
        gap:10
    }
}))

export {ConfirmationModal, showAlert};
